'use client';

import React from 'react';
import Link from 'next/link';
import { motion } from 'framer-motion';
import { ArrowLeft, Clock, ArrowRight, ArrowUpRight } from 'lucide-react';
import { usePublishedArticles, useArticleBySlug } from '../usePublishedArticles';
import type { ArticleBlock } from '../../admin-dashboard/components/ArticleEditor';
import { Connector } from '@/components/instrument';
import CtaLabel, { CTA_ASSESSMENT, CTA_BOOKING } from '@/components/ui/CtaLabel';
import EmailCaptureForm from '@/components/ui/EmailCaptureForm';

function renderBlocks(blocks: ArticleBlock[]) {
  return blocks.map((block, i) => {
    if (block.type === 'paragraph') {
      return (
        <p
          key={block.id}
          className="text-sm leading-relaxed my-4"
          style={{ color: 'var(--m-ink-5)', lineHeight: 1.75 }}
        >
          {block.content}
        </p>
      );
    }
    if (block.type === 'h2') {
      return (
        <h2
          key={block.id}
          className="font-bold uppercase mt-12 mb-5"
          style={{
            fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
            fontSize: 'clamp(16px, 1.6vw, 22px)',
            color: 'var(--m-ink)',
            letterSpacing: '0.02em',
            lineHeight: 1.2,
            paddingTop: 12,
            borderTop: '1px solid rgba(var(--m-ink-rgb),0.08)',
          }}
        >
          {block.content}
        </h2>
      );
    }
    if (block.type === 'h3') {
      return (
        <h3
          key={block.id}
          className="font-bold mt-8 mb-3"
          style={{
            fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
            fontSize: 'clamp(14px, 1.2vw, 17px)',
            color: 'var(--m-ink)',
            letterSpacing: '0.01em',
          }}
        >
          {block.content}
        </h3>
      );
    }
    if (block.type === 'quote') {
      return (
        <blockquote
          key={block.id}
          className="text-sm italic my-6"
          style={{
            borderLeft: '3px solid var(--m-ink)',
            paddingLeft: 16,
            color: 'var(--m-ink-5)',
            lineHeight: 1.7,
          }}
        >
          {block.content}
        </blockquote>
      );
    }
    if (block.type === 'callout') {
      return (
        <div
          key={block.id}
          className="my-6 p-4"
          style={{
            background: 'rgba(var(--m-ink-rgb),0.03)',
            borderLeft: '3px solid var(--m-ink)',
          }}
        >
          <p className="text-sm font-semibold" style={{ color: 'var(--m-ink)', lineHeight: 1.7 }}>
            {block.content}
          </p>
        </div>
      );
    }
    if (block.type === 'code') {
      return (
        <pre
          key={block.id}
          className="text-xs p-4 overflow-x-auto my-4"
          style={{
            background: 'rgba(var(--m-ink-rgb),0.04)',
            border: '1px solid rgba(var(--m-ink-rgb),0.08)',
            color: 'var(--m-ink-3)',
            fontFamily: 'monospace',
          }}
        >
          {block.content}
        </pre>
      );
    }
    if (block.type === 'list') {
      return (
        <ul key={block.id} className="space-y-3 my-5">
          {(block.listItems || []).map((item, j) => (
            <li
              key={j}
              className="flex items-start gap-3 text-sm leading-relaxed"
              style={{ color: 'var(--m-ink-5)' }}
            >
              <div
                className="flex-shrink-0 mt-2"
                style={{ width: 4, height: 4, background: 'var(--m-ink)', flexShrink: 0 }}
              />
              {item}
            </li>
          ))}
        </ul>
      );
    }
    if (block.type === 'numbered-list') {
      return (
        <ol key={block.id} className="space-y-3 my-5">
          {(block.listItems || []).map((item, j) => (
            <li
              key={j}
              className="flex items-start gap-3 text-sm leading-relaxed"
              style={{ color: 'var(--m-ink-5)' }}
            >
              <span
                className="font-mono font-bold flex-shrink-0"
                style={{ color: 'var(--m-ink)', fontSize: 11, minWidth: 20 }}
              >
                {j + 1}.
              </span>
              {item}
            </li>
          ))}
        </ol>
      );
    }
    if (block.type === 'divider') {
      return (
        <hr
          key={block.id}
          className="my-8"
          style={{ border: 'none', borderTop: '1px solid rgba(var(--m-ink-rgb),0.1)' }}
        />
      );
    }
    if (block.type === 'table' && block.tableHeaders && block.tableRows) {
      return (
        <div key={block.id} className="my-6 overflow-x-auto">
          <table
            className="w-full text-sm"
            style={{ borderCollapse: 'collapse', border: '1px solid rgba(var(--m-ink-rgb),0.1)' }}
          >
            <thead>
              <tr style={{ background: 'var(--m-ink)' }}>
                {block.tableHeaders.map((h, j) => (
                  <th
                    key={j}
                    className="text-left px-4 py-2.5 font-mono uppercase"
                    style={{
                      fontSize: 10,
                      color: 'var(--m-bg)',
                      letterSpacing: '0.08em',
                      borderRight: '1px solid rgba(var(--m-on-ink-rgb),0.1)',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.tableRows.map((row, j) => (
                <tr
                  key={j}
                  style={{
                    borderTop: '1px solid rgba(var(--m-ink-rgb),0.07)',
                    background: j % 2 === 0 ? 'var(--m-bg)' : 'rgba(var(--m-ink-rgb),0.02)',
                  }}
                >
                  {row.map((cell, k) => (
                    <td
                      key={k}
                      className="px-4 py-2.5"
                      style={{
                        color: 'var(--m-ink-5)',
                        borderRight: '1px solid rgba(var(--m-ink-rgb),0.07)',
                      }}
                    >
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    if (block.type === 'image' && block.imageUrl) {
      return (
        <figure key={block.id} className="my-6">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={block.imageUrl}
            alt={block.imageAlt || 'Article image'}
            className="w-full"
            style={{ border: '1px solid rgba(var(--m-ink-rgb),0.08)' }}
          />
          {block.imageCaption && (
            <figcaption
              className="text-xs mt-2 font-mono"
              style={{ color: 'var(--m-ink-6)', letterSpacing: '0.05em' }}
            >
              {block.imageCaption}
            </figcaption>
          )}
        </figure>
      );
    }
    return null;
  });
}

function renderMarkdown(content: string) {
  const sections = content.trim().split('\n\n').filter(Boolean);
  return sections.map((section, i) => {
    if (section.startsWith('## ')) {
      return (
        <h2
          key={i}
          className="font-bold uppercase mt-12 mb-5"
          style={{
            fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
            fontSize: 'clamp(16px, 1.6vw, 22px)',
            color: 'var(--m-ink)',
            letterSpacing: '0.02em',
            lineHeight: 1.2,
            paddingTop: 12,
            borderTop: '1px solid rgba(var(--m-ink-rgb),0.08)',
          }}
        >
          {section.replace('## ', '')}
        </h2>
      );
    }
    if (section.startsWith('### ')) {
      return (
        <h3
          key={i}
          className="font-bold mt-8 mb-3"
          style={{
            fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
            fontSize: 'clamp(14px, 1.2vw, 17px)',
            color: 'var(--m-ink)',
            letterSpacing: '0.01em',
          }}
        >
          {section.replace('### ', '')}
        </h3>
      );
    }
    if (section.startsWith('- ')) {
      const items = section.split('\n').filter((l) => l.startsWith('- '));
      return (
        <ul key={i} className="space-y-3 my-5">
          {items.map((item, j) => (
            <li
              key={j}
              className="flex items-start gap-3 text-sm leading-relaxed"
              style={{ color: 'var(--m-ink-5)' }}
            >
              <div
                className="flex-shrink-0 mt-2"
                style={{ width: 4, height: 4, background: 'var(--m-ink)', flexShrink: 0 }}
              />
              {item.replace('- ', '')}
            </li>
          ))}
        </ul>
      );
    }
    if (/^\d+\./.test(section)) {
      const items = section.split('\n').filter((l) => /^\d+\./.test(l));
      return (
        <ol key={i} className="space-y-3 my-5">
          {items.map((item, j) => (
            <li
              key={j}
              className="flex items-start gap-3 text-sm leading-relaxed"
              style={{ color: 'var(--m-ink-5)' }}
            >
              <span
                className="font-mono font-bold flex-shrink-0"
                style={{ color: 'var(--m-ink)', fontSize: 11, minWidth: 20 }}
              >
                {j + 1}.
              </span>
              {item.replace(/^\d+\.\s/, '')}
            </li>
          ))}
        </ol>
      );
    }
    return (
      <p
        key={i}
        className="text-sm leading-relaxed my-4"
        style={{ color: 'var(--m-ink-5)', lineHeight: 1.75 }}
      >
        {section}
      </p>
    );
  });
}

export default function ArticleDetailClient({ slug }: { slug: string }) {
  const article = useArticleBySlug(slug);
  const allArticles = usePublishedArticles();

  const related = allArticles
    .filter((a) => a.slug !== slug && a.category === article?.category)
    .slice(0, 2);
  const otherRelated =
    related.length < 2
      ? allArticles
          .filter((a) => a.slug !== slug && !related.includes(a))
          .slice(0, 2 - related.length)
      : [];
  const relatedArticles = [...related, ...otherRelated].slice(0, 2);

  if (!article) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'var(--m-surface)' }}
      >
        <div className="text-center">
          <p
            className="font-mono uppercase mb-6"
            style={{ fontSize: 9, color: 'var(--m-ink-6)', letterSpacing: '0.22em' }}
          >
            404 — Not Found
          </p>
          <h1
            className="font-bold uppercase mb-6"
            style={{
              fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
              fontSize: 'clamp(22px, 3vw, 32px)',
              color: 'var(--m-ink)',
              letterSpacing: '0.02em',
            }}
          >
            Article not found
          </h1>
          <Link
            href="/insights"
            className="inline-flex items-center gap-2 font-mono uppercase transition-all"
            style={{
              fontSize: 10,
              letterSpacing: '0.12em',
              padding: '10px 20px',
              border: '1px solid var(--m-ink)',
              color: 'var(--m-ink)',
              background: 'transparent',
              textDecoration: 'none',
            }}
          >
            Back to Insights
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen m-ground">
      {/* Hero */}
      <section data-ground className="pt-32 pb-14" style={{ background: 'var(--m-ink)' }}>
        <div className="m-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              href="/insights"
              className="inline-flex items-center gap-2 font-mono uppercase mb-10 transition-all"
              style={{
                fontSize: 10,
                color: 'rgba(var(--m-on-ink-rgb),0.4)',
                letterSpacing: '0.14em',
                textDecoration: 'none',
                minHeight: 32,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.color = 'rgba(var(--m-on-ink-rgb),0.8)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.color = 'rgba(var(--m-on-ink-rgb),0.4)';
              }}
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to Insights
            </Link>

            <div className="max-w-3xl">
              {/* The category, joined to the title by the ring's own elbow —
                  the same mark that ties a label to a service on the hero. It
                  annotates something real, which is the whole licence for
                  reusing the motif here. */}
              <div className="flex items-center gap-1 mb-5">
                <Connector
                  from={{ x: 2, y: 20 }}
                  to={{ x: 30, y: 2 }}
                  elbowRun={16}
                  onDark
                  style={{ marginRight: 2 }}
                />
                <span
                  className="inline-flex items-center font-mono uppercase"
                  style={{
                    fontSize: 10,
                    letterSpacing: '0.14em',
                    padding: '4px 10px',
                    border: '1px solid rgba(var(--m-on-ink-rgb),0.15)',
                    color: 'rgba(var(--m-on-ink-rgb),0.5)',
                  }}
                >
                  {article.category}
                </span>
              </div>
              <h1
                className="font-bold uppercase leading-none mb-5"
                style={{
                  fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
                  fontSize: 'clamp(24px, 3.5vw, 48px)',
                  color: 'var(--m-bg)',
                  letterSpacing: '0.02em',
                  lineHeight: 1.05,
                }}
              >
                {article.title}
              </h1>
              <p
                className="text-sm leading-relaxed mb-7"
                style={{ color: 'rgba(var(--m-on-ink-rgb),0.5)', lineHeight: 1.7, maxWidth: 560 }}
              >
                {article.excerpt}
              </p>
              <div
                className="flex items-center gap-6 font-mono uppercase"
                style={{
                  fontSize: 10,
                  color: 'rgba(var(--m-on-ink-rgb),0.4)',
                  letterSpacing: '0.16em',
                }}
              >
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5" />
                  {article.readTime}
                </div>
                <span>{article.date}</span>
                <span>Noventi Labs</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <div className="m-container py-14">
        {/* The article column is capped at a readable measure rather than
            taking two thirds of a 1536px grid, which ran lines to about 115
            characters. Comfortable reading is 45-75; past 90 the eye loses
            its place coming back to the left margin. */}
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,680px)_minmax(300px,1fr)] gap-12 lg:gap-16">
          {/* Article content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="min-w-0"
          >
            <div
              className="p-8 lg:p-10"
              style={{ background: 'var(--m-bg)', border: '1px solid rgba(var(--m-ink-rgb),0.07)' }}
            >
              {article.blocks && article.blocks.length > 0
                ? renderBlocks(article.blocks)
                : renderMarkdown(article.content || '')}
            </div>

            {/* CTA at end of article */}
            <div className="mt-8 p-8" style={{ background: 'var(--m-ink)' }}>
              <div className="flex items-center gap-3 mb-4">
                <div
                  style={{ width: 20, height: 1, background: 'rgba(var(--m-on-ink-rgb),0.2)' }}
                />
                <span
                  className="font-mono uppercase"
                  style={{
                    fontSize: 9,
                    color: 'rgba(var(--m-on-ink-rgb),0.35)',
                    letterSpacing: '0.22em',
                  }}
                >
                  CTA — Next Step
                </span>
              </div>
              <h3
                className="font-bold uppercase mb-3"
                style={{
                  fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
                  fontSize: 'clamp(18px, 2vw, 26px)',
                  color: 'var(--m-bg)',
                  letterSpacing: '0.02em',
                }}
              >
                Ready to apply this to your business?
              </h3>
              <p
                className="text-sm mb-6"
                style={{ color: 'rgba(var(--m-on-ink-rgb),0.5)', lineHeight: 1.7, maxWidth: 440 }}
              >
                Book a free 30-minute consultation and we&apos;ll show you exactly how these
                strategies apply to your specific situation.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  href="/book-a-consultation"
                  className="inline-flex items-center justify-center gap-2 font-mono uppercase transition-all px-6 py-3"
                  style={{
                    background: 'var(--m-bg)',
                    color: 'var(--m-ink)',
                    fontSize: 10,
                    letterSpacing: '0.12em',
                    border: '1px solid var(--m-bg)',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'transparent';
                    (e.currentTarget as HTMLElement).style.color = 'var(--m-bg)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'var(--m-bg)';
                    (e.currentTarget as HTMLElement).style.color = 'var(--m-ink)';
                  }}
                >
                  <CtaLabel mobile={CTA_BOOKING} desktop="Book a Free Consultation" />{' '}
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
                <Link
                  href="/business-assessment"
                  className="inline-flex items-center justify-center gap-2 font-mono uppercase transition-all px-6 py-3"
                  style={{
                    background: 'transparent',
                    color: 'rgba(var(--m-on-ink-rgb),0.6)',
                    fontSize: 10,
                    letterSpacing: '0.12em',
                    border: '1px solid rgba(var(--m-on-ink-rgb),0.2)',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor =
                      'rgba(var(--m-on-ink-rgb),0.5)';
                    (e.currentTarget as HTMLElement).style.color = 'var(--m-bg)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.borderColor =
                      'rgba(var(--m-on-ink-rgb),0.2)';
                    (e.currentTarget as HTMLElement).style.color = 'rgba(var(--m-on-ink-rgb),0.6)';
                  }}
                >
                  Take the Assessment
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Sidebar */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="min-w-0"
          >
            <div className="sticky top-28 space-y-6">
              {/* Newsletter. A reader who finishes an article is warmer than a
                  cold visitor, so the ask sits here rather than in the footer,
                  and records which article earned the sign-up. */}
              <div
                style={{
                  border: '1px solid rgba(var(--m-ink-rgb),0.12)',
                  background: 'var(--m-surface-2)',
                  padding: 20,
                }}
              >
                <p
                  className="font-mono uppercase mb-2"
                  style={{ fontSize: 9, color: 'var(--m-ink-6)', letterSpacing: '0.18em' }}
                >
                  Get the next one
                </p>
                <p
                  className="mb-4"
                  style={{ fontSize: 15, color: 'var(--m-ink-3)', lineHeight: 1.5 }}
                >
                  Practical growth guides for small and medium businesses. One email, no noise.
                </p>
                <EmailCaptureForm
                  source="newsletter"
                  context={article.slug}
                  buttonLabel="Subscribe"
                  successMessage="Subscribed. Talk soon."
                />
              </div>

              {relatedArticles.length > 0 && (
                <div>
                  <div
                    className="flex items-center gap-3 mb-5 pb-4"
                    style={{ borderBottom: '1px solid rgba(var(--m-ink-rgb),0.08)' }}
                  >
                    <div
                      style={{ width: 16, height: 1, background: 'rgba(var(--m-ink-rgb),0.2)' }}
                    />
                    <span
                      className="font-mono uppercase"
                      style={{ fontSize: 9, color: 'var(--m-ink-6)', letterSpacing: '0.18em' }}
                    >
                      Related Articles
                    </span>
                  </div>
                  <div className="space-y-px" style={{ background: 'rgba(var(--m-ink-rgb),0.06)' }}>
                    {relatedArticles.map((rel) => (
                      <Link
                        key={rel.id}
                        href={`/insights/${rel.slug}`}
                        className="group block p-4 transition-colors"
                        style={{ background: 'var(--m-bg)', textDecoration: 'none' }}
                        onMouseEnter={(e) => {
                          (e.currentTarget as HTMLElement).style.background = 'var(--m-surface-2)';
                        }}
                        onMouseLeave={(e) => {
                          (e.currentTarget as HTMLElement).style.background = 'var(--m-bg)';
                        }}
                      >
                        <span
                          className="inline-flex items-center font-mono uppercase mb-2"
                          style={{
                            fontSize: 8,
                            letterSpacing: '0.12em',
                            padding: '3px 8px',
                            border: '1px solid rgba(var(--m-ink-rgb),0.1)',
                            color: 'var(--m-muted-2)',
                          }}
                        >
                          {rel.category}
                        </span>
                        <p
                          className="text-sm font-bold leading-snug mb-2 transition-colors"
                          style={{
                            fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
                            color: 'var(--m-ink)',
                            lineHeight: 1.35,
                            letterSpacing: '0.01em',
                          }}
                        >
                          {rel.title}
                        </p>
                        <div
                          className="flex items-center gap-1.5 font-mono"
                          style={{ fontSize: 10, color: 'var(--m-ink-6)' }}
                        >
                          <Clock className="w-3 h-3" />
                          {rel.readTime}
                          <ArrowUpRight className="w-3 h-3 ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div className="p-6" style={{ background: 'var(--m-ink)' }}>
                <div className="flex items-center gap-2 mb-4">
                  <div
                    style={{ width: 14, height: 1, background: 'rgba(var(--m-on-ink-rgb),0.2)' }}
                  />
                  <span
                    className="font-mono uppercase"
                    style={{
                      fontSize: 8,
                      color: 'rgba(var(--m-on-ink-rgb),0.35)',
                      letterSpacing: '0.18em',
                    }}
                  >
                    Free Assessment
                  </span>
                </div>
                <p
                  className="font-bold uppercase mb-3"
                  style={{
                    fontFamily: "'Archivo Narrow', 'Roboto Condensed', sans-serif",
                    fontSize: 15,
                    color: 'var(--m-bg)',
                    letterSpacing: '0.02em',
                    lineHeight: 1.2,
                  }}
                >
                  How does your business score?
                </p>
                <p
                  className="text-xs mb-5"
                  style={{ color: 'rgba(var(--m-on-ink-rgb),0.45)', lineHeight: 1.7 }}
                >
                  Get a personalised growth report in 5 minutes.
                </p>
                <Link
                  href="/business-assessment"
                  className="flex items-center justify-center gap-2 font-mono uppercase transition-all py-2.5 px-4"
                  style={{
                    background: 'var(--m-bg)',
                    color: 'var(--m-ink)',
                    fontSize: 9,
                    letterSpacing: '0.12em',
                    border: '1px solid var(--m-bg)',
                    textDecoration: 'none',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'transparent';
                    (e.currentTarget as HTMLElement).style.color = 'var(--m-bg)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.background = 'var(--m-bg)';
                    (e.currentTarget as HTMLElement).style.color = 'var(--m-ink)';
                  }}
                >
                  Start Assessment <ArrowRight className="w-3 h-3" />
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
